import {
  type Achsenseite,
  HOECHSTENS_KENNZAHLEN_AUFGETEILT,
  achseVon,
  achsengrund,
  einheitenVon,
} from '@crm/kontrakte/src/achsen';

import type {
  Berichtsbeschreibung,
  Gruppierungskennung,
  Kataloggruppierung,
  Katalogkennzahl,
} from '../api/berichte';

/**
 * DIE AUSWAHL LINKS IM BAUKASTEN (AUS-16, Auftrag CRM-42) — ohne Oberflaeche.
 *
 * Kennzahlen und Gruppierung werden ueber eine Suche gewaehlt. Was nicht
 * passt, bleibt in der Liste und sagt warum — ausgegraut statt still weg:
 *
 *   - eine Kennzahl, die die gesetzte Gruppierung oder einen gesetzten Filter
 *     nicht kennt, oder die keine Achse mehr findet (achsen.ts — dieselbe
 *     Regel, nach der der Server prueft);
 *   - eine Gruppierung, die nicht jede gewaehlte Kennzahl traegt;
 *   - eine Aufteilung (Auftrag CRM-46), die nicht jede Kennzahl als Merkmal
 *     traegt, die schon die Gruppierung ist oder der die Gruppierung fehlt;
 *   - eine vorbereitete Kennzahl ohne Daten (Auftrag CRM-44) — mit dem Grund,
 *     womit ihre Daten kommen.
 *
 * Die Achse einer Kennzahl folgt aus der Liste (`achseVon`): die Einheit der
 * ersten links, eine andere rechts.
 */

export const BEREICHSNAMEN: Readonly<Record<Katalogkennzahl['bereich'], string>> = {
  faktura: 'Faktura',
  vertrieb: 'Vertrieb',
  service: 'Service',
  betrieb: 'Betrieb',
  ressourcen: 'Ressourcen',
};

/** Die Einheit in Worten; eine neue Einheit steht mit ihrem Namen da, bis sie hier einen bekommt. */
const EINHEITSNAMEN: Readonly<Partial<Record<string, string>>> = { EUR: 'Euro' };

export function einheitText(einheit: string): string {
  return EINHEITSNAMEN[einheit] ?? einheit;
}

export const ACHSENNAMEN: Readonly<Record<Achsenseite, string>> = { links: 'linke Achse', rechts: 'rechte Achse' };

/** Trifft die Suche einen der Texte? Gross- und Kleinschreibung zaehlen nicht; leer trifft alles. */
export function trifft(suche: string, texte: readonly string[]): boolean {
  const s = suche.trim().toLocaleLowerCase('de');
  return s === '' || texte.some((t) => t.toLocaleLowerCase('de').includes(s));
}

/** Eine Kennzahl wird nach Name, Bereich und Beschreibung gefunden. */
export function kennzahlTrifft(k: Katalogkennzahl, suche: string): boolean {
  return trifft(suche, [k.name, BEREICHSNAMEN[k.bereich], k.definition.zaehlt, k.definition.wann, k.definition.nicht]);
}

/** Eine Gruppierung nach Name und danach, ob sie eine Zeit oder ein Merkmal ist. */
export function gruppierungTrifft(g: Kataloggruppierung, suche: string): boolean {
  return trifft(suche, [g.name, g.kennung, g.zeitlich ? 'Zeit' : 'Merkmal']);
}

/**
 * Auftrag CRM-44: die Kennzahlen, die rechnen. Vorbereitete stehen in der
 * Suche, sind aber nirgends waehlbar — nicht als Vorgabe, nicht als Ziel.
 */
export function bereiteKennzahlen(ks: readonly Katalogkennzahl[]): readonly Katalogkennzahl[] {
  return ks.filter((k) => k.zustand === 'bereit');
}

/**
 * Auftrag CRM-44: der Grund, warum eine vorbereitete Kennzahl gesperrt ist —
 * sie hat noch keine Daten und zeigt deshalb keine Zahl, auch keine Null.
 */
export function vorbereitetGrund(k: Katalogkennzahl): string | null {
  return k.zustand === 'vorbereitet' ? `Vorbereitet, noch ohne Daten. ${k.grund ?? ''}`.trimEnd() : null;
}

/** Die gewaehlten Kennzahlen des Katalogs, in der Folge der Beschreibung. */
export function gewaehlteKennzahlen(
  katalog: readonly Katalogkennzahl[],
  b: Berichtsbeschreibung,
): readonly Katalogkennzahl[] {
  return b.kennzahlen.flatMap((kennung) => katalog.filter((k) => k.kennung === kennung));
}

/**
 * Warum `k` nicht zum Bericht dazukommen kann — oder null. Eine schon
 * gewaehlte Kennzahl hat keinen Grund: Sie steht in der Liste.
 */
export function kennzahlgrund(
  k: Katalogkennzahl,
  b: Berichtsbeschreibung,
  gewaehlt: readonly Katalogkennzahl[],
  gruppenname: (g: string) => string,
): string | null {
  const ohneDaten = vorbereitetGrund(k);
  if (ohneDaten !== null) return ohneDaten;
  if (b.kennzahlen.includes(k.kennung)) return null;
  if (b.gruppierung !== null && !k.gruppierungen.includes(b.gruppierung as Gruppierungskennung)) {
    return `Lässt sich nicht nach ${gruppenname(b.gruppierung)} gruppieren.`;
  }
  const fremd = b.filter.find((f) => !k.filter.includes(f.nach as Gruppierungskennung));
  if (fremd !== undefined) return `Lässt sich nicht nach ${gruppenname(fremd.nach)} filtern.`;
  // Auftrag CRM-46: Die Aufteilung muss jede Kennzahl als Merkmal tragen, und es bleiben zehn Reihen.
  if (b.aufteilung != null) {
    if (!k.filter.includes(b.aufteilung as Gruppierungskennung)) {
      return `Lässt sich nicht nach ${gruppenname(b.aufteilung)} aufteilen.`;
    }
    if (gewaehlt.length >= HOECHSTENS_KENNZAHLEN_AUFGETEILT) {
      return `Aufgeteilt trägt ein Bericht höchstens ${String(HOECHSTENS_KENNZAHLEN_AUFGETEILT)} Kennzahlen.`;
    }
  }
  // AUS-16: hoechstens zwei Einheiten, je Einheit hoechstens fuenf (achsen.ts).
  return achsengrund(
    gewaehlt.map((x) => ({ einheit: einheitText(x.einheit) })),
    { einheit: einheitText(k.einheit) },
  );
}

/** Warum die Gruppierung nicht geht — die Kennzahlen, die sie nicht tragen — oder null. */
export function gruppierungsgrund(g: Kataloggruppierung, gewaehlt: readonly Katalogkennzahl[]): string | null {
  const ohne = gewaehlt.filter((k) => !k.gruppierungen.includes(g.kennung));
  if (ohne.length === 0) return null;
  const namen = ohne.map((k) => `„${k.name}“`).join(', ');
  return ohne.length === 1 ? `${namen} lässt sich so nicht gruppieren.` : `${namen} lassen sich so nicht gruppieren.`;
}

/**
 * Warum nicht nach `g` aufgeteilt werden kann — oder null (Auftrag CRM-46).
 * Dieselben Regeln wie `pruefeBericht` im Server: ein Merkmal, das jede
 * Kennzahl traegt (also auch als Filter), nicht die Gruppierung selbst, nur
 * mit einer Gruppierung und mit hoechstens fuenf Kennzahlen.
 */
export function aufteilungsgrund(
  g: Kataloggruppierung,
  gewaehlt: readonly Katalogkennzahl[],
  b: Berichtsbeschreibung,
): string | null {
  if (g.zeitlich) return 'Eine Zeit steht auf der Achse; aufgeteilt wird nach einem Merkmal.';
  if (gewaehlt.length === 0) return 'Erst eine Kennzahl wählen.';
  if (b.gruppierung === null) return 'Erst gruppieren — aufgeteilt wird jede Gruppe.';
  if (g.kennung === b.gruppierung) return 'Danach wird schon gruppiert.';
  const ohne = gewaehlt.filter((k) => !k.filter.includes(g.kennung));
  if (ohne.length > 0) {
    const namen = ohne.map((k) => `„${k.name}“`).join(', ');
    return ohne.length === 1 ? `${namen} lässt sich so nicht aufteilen.` : `${namen} lassen sich so nicht aufteilen.`;
  }
  if (gewaehlt.length > HOECHSTENS_KENNZAHLEN_AUFGETEILT) {
    return `Aufgeteilt trägt ein Bericht höchstens ${String(HOECHSTENS_KENNZAHLEN_AUFGETEILT)} Kennzahlen.`;
  }
  return null;
}

/** Filtern laesst sich nur nach Merkmalen, die JEDE gewaehlte Kennzahl kennt. */
export function gemeinsameFilter(gewaehlt: readonly Katalogkennzahl[]): readonly Gruppierungskennung[] {
  const [erste, ...rest] = gewaehlt;
  return erste === undefined ? [] : erste.filter.filter((g) => rest.every((k) => k.filter.includes(g)));
}

/** Die Achsen der Auswahl: je Kennzahl ihre Seite, dazu ob es eine rechte gibt. */
export function achsenDerAuswahl(gewaehlt: readonly Katalogkennzahl[]): {
  readonly seiten: readonly Achsenseite[];
  readonly zweiAchsen: boolean;
} {
  return {
    seiten: gewaehlt.map((_, i) => achseVon(gewaehlt, i)),
    zweiAchsen: einheitenVon(gewaehlt).length > 1,
  };
}
